import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrg } from "@/hooks/useOrg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

const FONT_OPTIONS: { label: string; value: string }[] = [
  {
    label: "Sans-serif",
    value:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  { label: "Serif", value: "Georgia, 'Times New Roman', Times, serif" },
  {
    label: "Monospace",
    value:
      "'SF Mono', SFMono-Regular, Menlo, Consolas, 'Courier New', monospace",
  },
  {
    label: "Rounded",
    value: "system-ui, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
];

interface BrandingData {
  font: string;
  bg_color: string;
  text_color: string;
  btn_color: string;
  btn_text_color: string;
  btn_border_radius: number;
  padding_x: number;
}

const defaults: BrandingData = {
  font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  bg_color: "#1a1a1a",
  text_color: "#ffffff",
  btn_color: "#ffffff",
  btn_text_color: "#000000",
  btn_border_radius: 6,
  padding_x: 16,
};

export default function Branding() {
  const { orgId } = useOrg();
  const [data, setData] = useState<BrandingData>(defaults);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exists, setExists] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    supabase
      .from("org_branding")
      .select("*")
      .eq("org_id", orgId)
      .maybeSingle()
      .then(({ data: branding }) => {
        if (branding) {
          setData({
            font: branding.font,
            bg_color: branding.bg_color,
            text_color: branding.text_color,
            btn_color: branding.btn_color,
            btn_text_color: branding.btn_text_color,
            btn_border_radius: branding.btn_border_radius,
            padding_x: branding.padding_x,
          });
          setExists(true);
        }
        setLoading(false);
      });
  }, [orgId]);

  const update = (field: keyof BrandingData, value: string | number) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);

    if (exists) {
      const { error } = await supabase
        .from("org_branding")
        .update(data)
        .eq("org_id", orgId);
      if (error) toast.error(error.message);
      else toast.success("Branding saved");
    } else {
      const { error } = await supabase
        .from("org_branding")
        .insert({ ...data, org_id: orgId });
      if (error) toast.error(error.message);
      else {
        setExists(true);
        toast.success("Branding saved");
      }
    }
    setSaving(false);
  };

  if (loading)
    return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const fontFamily = data.font;

  return (
    <div className="flex h-full flex-col">
      {/* Live preview */}
      <div
        className="flex items-center justify-between gap-4 py-3"
        style={{
          backgroundColor: data.bg_color,
          fontFamily,
          paddingLeft: `${data.padding_x}px`,
          paddingRight: `${data.padding_x}px`,
        }}
      >
        <div className="flex items-center gap-3 flex-wrap min-w-0 flex-1">
          <span className="text-sm" style={{ color: data.text_color }}>
            This is how your banners will look by default
          </span>
        </div>
        <span
          className="inline-flex shrink-0 items-center px-3 py-1 text-xs font-medium"
          style={{
            backgroundColor: data.btn_color,
            color: data.btn_text_color,
            borderRadius: `${data.btn_border_radius}px`,
          }}
        >
          Button
        </span>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-8">
        <div className="mx-auto max-w-lg space-y-6">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Branding</h1>
            <p className="text-sm text-muted-foreground">
              Set default styles for new banners.
            </p>
          </div>

          <section className="space-y-4">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Default style
            </h3>

            <div className="space-y-2">
              <Label>Font</Label>
              <Select
                value={data.font}
                onValueChange={(v) => update("font", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.map((f) => (
                    <SelectItem key={f.label} value={f.value}>
                      <span style={{ fontFamily: f.value }}>{f.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <ColorField
                label="Background"
                value={data.bg_color}
                onChange={(v) => update("bg_color", v)}
              />
              <ColorField
                label="Text"
                value={data.text_color}
                onChange={(v) => update("text_color", v)}
              />
              <ColorField
                label="Button"
                value={data.btn_color}
                onChange={(v) => update("btn_color", v)}
              />
              <ColorField
                label="Button text"
                value={data.btn_text_color}
                onChange={(v) => update("btn_text_color", v)}
              />
            </div>

            <div className="space-y-2">
              <Label>Button border radius: {data.btn_border_radius}px</Label>
              <Slider
                value={[data.btn_border_radius]}
                onValueChange={([v]) => update("btn_border_radius", v)}
                min={0}
                max={24}
                step={1}
              />
            </div>

            <div className="space-y-2">
              <Label>Horizontal padding: {data.padding_x}px</Label>
              <Slider
                value={[data.padding_x]}
                onValueChange={([v]) => update("padding_x", v)}
                min={0}
                max={64}
                step={4}
              />
            </div>
          </section>

          <div className="pt-4">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save branding"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-9 cursor-pointer rounded border border-border bg-transparent p-0.5"
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="font-mono text-xs"
        />
      </div>
    </div>
  );
}
